/**
 * Material-UI styles hook for the EnhancedTableToolbar component.
 *
 * @module components/sim/EnhancedTableToolbar
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import {makeStyles, alpha} from '@material-ui/core/styles';

/**
 * A hook for defining and applying styles to a toolbar component.
 *
 * @typedef {Function} useToolbarStyles
 *
 * @description
 * Contains the style definitions for customizing and styling various elements
 * of a toolbar. The following style keys correspond to specific component areas:
 *
 * - `root`: Styles for the root toolbar container, including horizontal padding.
 * - `title`: Styles for the toolbar title, with flex growth and bold font weight.
 * - `search`: Styles for the search input container, including alignment, hover effect, and layout properties.
 * - `searchIcon`: Styles for the search icon within the search container, with alignment and pointer rule.
 * - `inputRoot`: Styles for the root container of the search input field, applying color and flex growth.
 * - `inputInput`: Styles for the inner content of the search input field, including padding.
 * - `select`: Styles for the select drop-down element, including padding and minimum width.
 * - `plusIcon`: Styles for the plus icon area, with margin adjustments.
 * - `addNewButton`: Styles for the "Add New" button, including padding, background color, hover effect, and layout properties.
 *
 * Theme spacing and palette APIs are utilized to ensure consistent spacing
 * and color scheme adherence based on the theme object.
 */
export const useToolbarStyles = makeStyles((theme) => ({
    root: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    title: {
        flexGrow: 1,
        fontWeight: 'bold',
    },
    search: {
        // position: "relative",
        display: 'flex',
        borderRadius: '5px',
        backgroundColor: alpha(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.25),
        },
        marginRight: 10,
        width: 300,
        border: '2px solid #e1e1e1',
        alignItems: 'center',
        justifyContent: 'center',
    },
    searchIcon: {
        padding: theme.spacing(0, 2),
        height: '100%',
        // position: "absolute",
        pointerEvents: 'none',
        // display: "flex",
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: '7px',
    },
    inputRoot: {
        color: 'inherit',
        flexGrow: 1,
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 1),
    },
    select: {
        minWidth: 200,
        padding: theme.spacing(0, 1, 0, 1),
        marginRight: 10,
    },
    plusIcon: {
        margin: '0 5px 3px 0',
    },
    addNewButton: {
        paddingLeft: '10px',
        color: 'white',
        minWidth: '10%',
        height: 36,
        fontSize: 12,
        whiteSpace: 'nowrap',
        paddingRight: 12,
        backgroundColor: '#284375',
        margin: '0px 5px',
        '&:hover': {
            backgroundColor: '#647087',
        },
    },
}));

/**
 * useTableStyles is a hook that generates a collection of Material-UI styling rules for various UI components used in a table and its associated elements.
 *
 * Properties:
 * - backdrop: Styles applied to the backdrop, controlling z-index and positioning.
 * - cancelButton: Styles applied to cancel button elements, specifying color.
 * - root: Styles applied to the root element, setting width and horizontal padding.
 * - thead: Styles applied to the table header, including background color and padding.
 * - heading: Styles for bold text headings, defining font weight and font size.
 * - bold: Styles for bolded elements with defined minimum and maximum widths.
 * - built: Styles for display-flex elements used in table rows.
 * - small: Styles for small-sized elements with fixed width and height.
 * - buttonSqr: Styles for square-shaped interactive buttons with padding and margin.
 * - buttonDelete: Styles for delete buttons with padding, background color, and text color.
 * - tableRow: Styles for table rows, enabling pointer cursor on hover.
 * - dialogDeleteBtn: Styles for delete buttons within dialogs, specifying red color.
 * - admin_note: Styles for admin note elements with fixed width.
 * - noData: Styles for "no data available" message, positioning it centrally.
 * - plus: Styles for plus-sign text, defining font size, weight, and padding.
 * - noAssign: Styles for "no assignment" element, adjusting margins.
 * - noAssignPara: Styles for paragraph text associated with "no assignment," defining font size.
 * - addButton: Styles for add buttons, setting font size, white space handling, and height.
 * - paper: Styles for paper components, setting height to 95%.
 * - hidePage: Styles for hidden page components, setting height and visibility.
 * - errorAlert: Styles for error alert boxes, defining background color.
 * - type: Styles for type elements, defining fixed width.
 * - created: Styles for created date elements, defining fixed width.
 * - pagination: Styles for pagination components, including spacing and selected-state appearance.
 * - paginationLabel: Styles for labels within the pagination section, setting padding and alignment.
 * - noDataFound: Styles for "no data found" messages, including margin, color, and font weight.
 * - levelCounter: Styles for circular counters representing levels, with a red background and white text.
 * - scriptCounter: Styles for counters associated with scripts, with a green background and white text.
 */
export const useTableStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: 1,
        position: 'inherit',
    },
    cancelButton: {
        color: '#313387',
    },
    root: {
        width: '100%',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },
    thead: {
        backgroundColor: '#e1e1e1',
        '& .MuiTableCell-head': {
            paddingTop: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
    },
    heading: {
        fontWeight: 'bold',
        fontSize: '0.7rem',
    },
    bold: {
        fontWeight: 'bold',
        minWidth: '12rem',
        maxWidth: '12rem',
    },
    built: {
        display: 'flex',
        alignItems: 'center',
        minWidth: '7rem',
    },
    small: {
        width: theme.spacing(4),
        height: theme.spacing(4),
    },
    buttonSqr: {
        padding: '5px',
        marginRight: '5px',
        minHeight: 0,
        minWidth: 0,
    },
    buttonDelete: {
        padding: '5px',
        marginRight: '5px',
        minHeight: 0,
        minWidth: 0,
        background: '#DC004E',
        color: '#fff',
    },
    tableRow: {
        cursor: 'pointer',
    },
    dialogDeleteBtn: {
        color: 'red',
    },
    admin_note: {
        maxWidth: '18rem',
        minWidth: '18rem',
    },
    noData: {
        position: 'absolute',
        top: '50%',
        width: '90%',
        textAlign: 'center',
    },
    plus: {
        fontSize: 16,
        fontWeight: 600,
        paddingRight: 5,
    },
    noAssign: {
        marginBottom: 0,
    },
    noAssignPara: {
        fontSize: 12,
    },
    addButton: {
        fontSize: 12,
        whiteSpace: 'nowrap',
        height: 34,
    },
    paper: {
        height: '95%',
    },
    hidePage: {
        height: '92%',
        visibility: 'hidden',
    },
    errorAlert: {
        backgroundColor: '#600727',
    },
    type: {
        minWidth: '7rem',
        maxWidth: '7rem',
    },
    created: {
        minWidth: '5rem',
        maxWidth: '5rem',
    },
    pagination: {
        padding: 13,
        float: 'right',
        '& .MuiPaginationItem-textPrimary.Mui-selected': {
            background: '#26344D',
        },
    },
    paginationLabel: {
        padding: 17,
        float: 'left',
    },
    noDataFound: {
        margin: 18,
        color: '#646462',
        fontWeight: 500,
    },
    levelCounter: {
        backgroundColor: 'red',
        color: 'white',
        marginTop: '-1em',
        borderRadius: '50%',
        width: 'auto',
        height: '100%',
        aspectRatio: '1',
        position: 'relative',
    },
    scriptCounter: {
        backgroundColor: 'green',
        color: 'white',
        marginTop: '1em',
        borderRadius: '50%',
        width: 'auto',
        height: '100%',
        aspectRatio: '1',
        position: 'relative',
    },
}));
